import { maxOperatorChartToolPoints } from "../constants";
import type { OperatorChartDataSliceOptions, OperatorTimeSeriesData, OperatorTimeSeriesDataSlice } from "../types";

/**
 * Returns a bounded, evenly distributed slice across the requested series so
 * a chart-data tool call cannot grow in direct proportion to the full chart.
 */
export const getOperatorTimeSeriesDataSlice = (
  data: OperatorTimeSeriesData,
  { seriesNames, startIndex, maxPoints }: OperatorChartDataSliceOptions
): OperatorTimeSeriesDataSlice => {
  const selectedSeries = seriesNames?.length
    ? data.series.filter(({ name }) => seriesNames.includes(name))
    : data.series;
  const boundedStartIndex = Math.max(0, Math.floor(startIndex ?? 0));
  const boundedMaxPoints = Math.min(
    maxOperatorChartToolPoints,
    Math.max(1, Math.floor(maxPoints ?? maxOperatorChartToolPoints))
  );
  const returnedSeries = selectedSeries.slice(0, boundedMaxPoints);
  const pointsPerSeries = Math.max(1, Math.floor(boundedMaxPoints / Math.max(1, returnedSeries.length)));

  const series = returnedSeries.map(({ data: seriesData, ...seriesMetadata }) => ({
    ...seriesMetadata,
    dataStartIndex: boundedStartIndex,
    totalPointCount: seriesData.length,
    data: seriesData.slice(boundedStartIndex, boundedStartIndex + pointsPerSeries),
  }));
  const returnedPointCount = series.reduce((total, { data: seriesData }) => total + seriesData.length, 0);

  return {
    type: data.type,
    xAxis: data.xAxis,
    series,
    page: {
      startIndex: boundedStartIndex,
      maxPoints: boundedMaxPoints,
      returnedPointCount,
      hasMore:
        series.some(({ dataStartIndex, data: seriesData, totalPointCount }) => {
          return dataStartIndex + seriesData.length < totalPointCount;
        }) || returnedSeries.length < selectedSeries.length,
      availableSeriesNames: data.series.map(({ name }) => name),
      omittedSeriesNames: selectedSeries.slice(returnedSeries.length).map(({ name }) => name),
    },
  };
};
